import { HORIZONTAL_GAP, PADDING, getNodeDimensions } from '../constants';
import { translatePathD } from '../elkEdgePathAdapter';
import { getNonMilestoneTaskIds } from '../../utils/milestoneUtils';
import { getBoundingBox, getChainBraceBoundingBox } from './elkGeometry';
import { layoutElkComponent } from './layoutElkComponent';

async function layoutSingleChain(elk, component, nodeDimensions) {
  const { positions, edgePaths, links } = await layoutElkComponent(
    elk,
    component.tasks,
    nodeDimensions,
  );
  const allPositions = {};
  Object.entries(positions).forEach(([id, pos]) => {
    allPositions[id] = {
      x: pos.x + PADDING,
      y: pos.y + PADDING,
    };
  });

  const allEdgePaths = {};
  const anchorId = component.tasks[0]?.id;
  if (anchorId && positions[anchorId] && allPositions[anchorId]) {
    const dx = allPositions[anchorId].x - positions[anchorId].x;
    const dy = allPositions[anchorId].y - positions[anchorId].y;
    Object.entries(edgePaths).forEach(([eid, d]) => {
      if (d) allEdgePaths[eid] = translatePathD(d, dx, dy);
    });
  }

  const bbox = getChainBraceBoundingBox(allPositions, nodeDimensions, component.tasks);
  return {
    positions: allPositions,
    edgePaths: allEdgePaths,
    links: links || [],
    chainInfo: [{
      id: component.id,
      taskIds: getNonMilestoneTaskIds(component.tasks),
      position: { x: PADDING, y: PADDING },
      width: bbox.width,
    }],
  };
}

async function layoutDisconnectedChains(elk, orderedComponents, nodeDimensions) {
  const allPositions = {};
  const allEdgePaths = {};
  const allLinks = [];
  const chainInfo = [];

  // Layout each disconnected chain in parallel (single shared ELK worker handles sequential layout).
  const componentLayouts = await Promise.all(
    orderedComponents.map(async (component) => {
      const { positions: rawPos, edgePaths, links } = await layoutElkComponent(
        elk,
        component.tasks,
        nodeDimensions,
      );
      const bbox = getBoundingBox(rawPos, nodeDimensions);

      const normalizedPositions = {};
      Object.entries(rawPos).forEach(([taskId, pos]) => {
        if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
        normalizedPositions[taskId] = {
          x: pos.x - bbox.minX,
          y: pos.y - bbox.minY + PADDING,
        };
      });

      return {
        component,
        rawPositions: rawPos,
        positions: normalizedPositions,
        bbox: getBoundingBox(normalizedPositions, nodeDimensions),
        edgePaths,
        links,
      };
    }),
  );

  const placedNodes = [];

  componentLayouts.forEach((layout) => {
    const { component, positions, bbox, edgePaths, rawPositions, links } = layout;

    const chainNodes = [];
    Object.entries(positions).forEach(([taskId, pos]) => {
      const dims = getNodeDimensions(taskId, nodeDimensions);
      chainNodes.push({
        id: taskId,
        x: pos.x,
        y: pos.y,
        width: dims.width,
        height: dims.height,
        right: pos.x + dims.width,
        bottom: pos.y + dims.height,
      });
    });

    let targetX = PADDING;
    if (placedNodes.length > 0) {
      let maxRequiredX = PADDING;
      chainNodes.forEach((chainNode) => {
        const verticallyOverlapping = placedNodes.filter((placed) => chainNode.y < placed.bottom && chainNode.bottom > placed.y);
        if (verticallyOverlapping.length > 0) {
          let rightmostEdge = -Infinity;
          verticallyOverlapping.forEach((placed) => {
            rightmostEdge = Math.max(rightmostEdge, placed.right);
          });
          const minAllowedX = rightmostEdge + HORIZONTAL_GAP;
          const nodeOffsetFromChainMin = chainNode.x - bbox.minX;
          const chainMinXForThisNode = minAllowedX - nodeOffsetFromChainMin;
          maxRequiredX = Math.max(maxRequiredX, chainMinXForThisNode);
        }
      });
      targetX = maxRequiredX;
    }

    const offsetX = targetX - bbox.minX;

    Object.entries(positions).forEach(([taskId, pos]) => {
      const finalPos = {
        x: pos.x + offsetX,
        y: pos.y,
      };
      allPositions[taskId] = finalPos;

      const dims = getNodeDimensions(taskId, nodeDimensions);
      placedNodes.push({
        id: taskId,
        x: finalPos.x,
        y: finalPos.y,
        width: dims.width,
        height: dims.height,
        right: finalPos.x + dims.width,
        bottom: finalPos.y + dims.height,
      });
    });

    const anchorId = component.tasks[0]?.id;
    if (anchorId && rawPositions?.[anchorId] && allPositions[anchorId]) {
      const dx = allPositions[anchorId].x - rawPositions[anchorId].x;
      const dy = allPositions[anchorId].y - rawPositions[anchorId].y;
      Object.entries(edgePaths).forEach(([eid, d]) => {
        if (d) allEdgePaths[eid] = translatePathD(d, dx, dy);
      });
    }
    (links || []).forEach((link) => allLinks.push(link));

    const finalBbox = getChainBraceBoundingBox(
      Object.fromEntries(component.tasks.filter((task) => allPositions[task.id]).map((task) => [task.id, allPositions[task.id]])),
      nodeDimensions,
      component.tasks,
    );

    chainInfo.push({
      id: component.id,
      taskIds: getNonMilestoneTaskIds(component.tasks),
      position: { x: targetX, y: PADDING },
      width: finalBbox.width,
    });
  });

  return { positions: allPositions, edgePaths: allEdgePaths, links: allLinks, chainInfo };
}

export async function assembleComponentLayouts(elk, orderedComponents, nodeDimensions) {
  if (orderedComponents.length <= 1) {
    return layoutSingleChain(elk, orderedComponents[0], nodeDimensions);
  }
  return layoutDisconnectedChains(elk, orderedComponents, nodeDimensions);
}
